import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  ArrowLeftIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  PhotoIcon,
  ArrowPathIcon,
  UserIcon,
  TagIcon,
  ArchiveBoxIcon,
  MapPinIcon,
  QrCodeIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import api from '../../utils/api';

const InventoryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  
  // Fetch item details
  const {
    data: item,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery(['inventory', id], async () => {
    const response = await api.get(`/api/inventory/${id}`);
    return response.data;
  });
  
  // Fetch lists for dropdown
  const { data: lists } = useQuery('activeLists', async () => {
    const response = await api.get('/api/lists/active');
    return response.data;
  });
  
  // Fetch consignors for dropdown
  const { data: consignors } = useQuery('activeConsignors', async () => {
    const response = await api.get('/api/consignors/active');
    return response.data;
  });
  
  // Mutation for updating item
  const updateItemMutation = useMutation(
    (data) => api.put(`/api/inventory/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory', id]);
        setIsEditing(false);
      },
      onError: (err) => {
        alert(err.response?.data?.message || 'Failed to update item');
      }
    }
  );
  
  // Mutation for deleting item
  const deleteItemMutation = useMutation(
    () => api.delete(`/api/inventory/${id}`),
    {
      onSuccess: () => {
        navigate('/app/inventory');
      },
      onError: (err) => {
        alert(err.response?.data?.message || 'Failed to delete item');
      }
    }
  );
  
  // Mutation for deleting an image
  const deleteImageMutation = useMutation(
    (imageId) => api.delete(`/api/inventory/${id}/images/${imageId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory', id]);
      },
      onError: (err) => {
        alert(err.response?.data?.message || 'Failed to delete image');
      }
    }
  );
  
  // Mutation for setting primary image
  const setPrimaryImageMutation = useMutation(
    (imageId) => api.put(`/api/inventory/${id}/images/${imageId}/primary`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['inventory', id]);
      },
      onError: (err) => {
        alert(err.response?.data?.message || 'Failed to set primary image');
      }
    }
  );
  
  // Initialize edit form when item data is available
  useEffect(() => {
    if (item && !editData) {
      setEditData({
        title: item.title,
        description: item.description || '',
        category: item.category || '',
        upc: item.upc || '',
        binCode: item.binCode || '',
        locationCode: item.locationCode || '',
        status: item.status,
        listId: item.listId || '',
        consignorId: item.consignorId || '',
        estimatedValue: item.estimatedValue || '',
        acquisitionCost: item.acquisitionCost || '',
        quantity: item.quantity || 1,
        tags: item.tags?.join(', ') || ''
      });
    }
  }, [item, editData]);
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
    }));
  };
  
  // Handle update item form submission
  const handleUpdateItem = (e) => {
    e.preventDefault();
    
    // Process tags from comma-separated string to array
    const formattedData = {
      ...editData,
      tags: editData.tags ? editData.tags.split(',').map(tag => tag.trim()) : []
    };
    
    updateItemMutation.mutate(formattedData);
  };
  
  // Handle delete item
  const handleDeleteItem = () => {
    if (window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      deleteItemMutation.mutate();
    }
  };
  
  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('images', files[i]);
    }
    
    try {
      await api.post(`/api/inventory/${id}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Refetch item to get updated images
      refetch();
    } catch (err) {
      console.error('Image upload error:', err);
      setUploadError(err.response?.data?.message || 'Failed to upload images');
    } finally {
      setIsUploading(false);
      // Reset the file input
      e.target.value = null;
    }
  };
  
  // Handle delete image
  const handleDeleteImage = (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      deleteImageMutation.mutate(imageId);
    }
  };
  
  // Handle set primary image
  const handleSetPrimaryImage = (imageId) => {
    setPrimaryImageMutation.mutate(imageId);
  };
  
  // Format currency value
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '--';
    return `$${parseFloat(value).toFixed(2)}`;
  };
  
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto py-6 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (isError) {
    return (
      <div className="max-w-7xl mx-auto py-6 text-center">
        <div className="bg-red-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-red-800">Error loading item</h3>
          <p className="mt-2 text-sm text-red-700">
            {error?.message || 'Failed to load inventory item'}
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate('/app/inventory')}
              className="btn-secondary"
            >
              <ArrowLeftIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
              Back to Inventory
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button and actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 p-1 rounded-md hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-6 w-6 text-gray-500" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-900 truncate">{item.title}</h1>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="btn-secondary"
          >
            {isEditing ? (
              'Cancel Edit'
            ) : (
              <>
                <PencilIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                Edit Item
              </>
            )}
          </button>
          <button
            onClick={handleDeleteItem}
            className="btn-danger"
          >
            <TrashIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
            Delete
          </button>
        </div>
      </div>
      
      <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-3">
        {/* Images section */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow sm:rounded-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Images</h3>
            </div>
            
            {/* Image gallery */}
            <div className="p-4">
              {item.images?.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {item.images.map((image) => (
                    <div key={image.id} className="relative group">
                      <div className="aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-gray-200">
                        <img
                          src={image.path}
                          alt={item.title}
                          className="object-cover h-full w-full"
                        />
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex space-x-2">
                          {!image.isPrimary && (
                            <button
                              onClick={() => handleSetPrimaryImage(image.id)}
                              className="p-1 rounded-full bg-white text-green-600 hover:text-green-800"
                              title="Set as primary image"
                            >
                              <CheckCircleIcon className="h-5 w-5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteImage(image.id)}
                            className="p-1 rounded-full bg-white text-red-600 hover:text-red-800"
                            title="Delete image"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      {image.isPrimary && (
                        <div className="absolute top-1 right-1">
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                            Primary
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No images yet</p>
                </div>
              )}
              
              {/* Upload button */}
              <div className="mt-4">
                <label
                  htmlFor="image-upload"
                  className={`btn-secondary w-full flex justify-center ${isUploading ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isUploading ? (
                    <>
                      <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <PhotoIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                      Upload Images
                    </>
                  )}
                </label>
                <input
                  id="image-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                  className="hidden"
                />
                {uploadError && (
                  <p className="mt-2 text-sm text-red-600">{uploadError}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Sidebar info cards */}
          <div className="mt-6 bg-white shadow sm:rounded-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Item Details</h3>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex justify-between items-start">
                <div className="text-sm font-medium text-gray-500">Status</div>
                <div className="text-sm text-right">
                  {item.status === 'AVAILABLE' ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                      <CheckCircleIcon className="-ml-0.5 mr-1 h-4 w-4" />
                      Available
                    </span>
                  ) : item.status === 'RESERVED' ? (
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                      <CheckCircleIcon className="-ml-0.5 mr-1 h-4 w-4" />
                      Reserved
                    </span>
                  ) : item.status === 'SOLD' ? (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                      <CheckCircleIcon className="-ml-0.5 mr-1 h-4 w-4" />
                      Sold
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                      <XCircleIcon className="-ml-0.5 mr-1 h-4 w-4" />
                      Archived
                    </span>
                  )}
                </div>
              </div>
              
              {item.upc && (
                <div className="flex justify-between items-start">
                  <div className="text-sm font-medium text-gray-500 flex items-center">
                    <QrCodeIcon className="h-4 w-4 mr-1" />
                    UPC/Barcode
                  </div>
                  <div className="text-sm text-gray-900 text-right">{item.upc}</div>
                </div>
              )}
              
              <div className="flex justify-between items-start">
                <div className="text-sm font-medium text-gray-500 flex items-center">
                  <ArchiveBoxIcon className="h-4 w-4 mr-1" />
                  List
                </div>
                <div className="text-sm text-gray-900 text-right">{item.listName || 'None'}</div>
              </div>
              
              {(item.binCode || item.locationCode) && (
                <div className="flex justify-between items-start">
                  <div className="text-sm font-medium text-gray-500 flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    Location
                  </div>
                  <div className="text-sm text-gray-900 text-right">
                    {item.binCode && `Bin: ${item.binCode}`}
                    {item.binCode && item.locationCode && ', '}
                    {item.locationCode && `Location: ${item.locationCode}`}
                  </div>
                </div>
              )}
              
              {item.consignorName && (
                <div className="flex justify-between items-start">
                  <div className="text-sm font-medium text-gray-500 flex items-center">
                    <UserIcon className="h-4 w-4 mr-1" />
                    Consignor
                  </div>
                  <div className="text-sm text-primary-600 text-right">
                    <Link to={`/app/consignors/${item.consignorId}`}>
                      {item.consignorName} ({item.consignorCode || 'No Code'})
                    </Link>
                  </div>
                </div>
              )}
              
              <div className="flex justify-between items-start">
                <div className="text-sm font-medium text-gray-500 flex items-center">
                  <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                  Value
                </div>
                <div className="text-sm text-gray-900 text-right">
                  {formatCurrency(item.estimatedValue)}
                </div>
              </div>
              
              {item.acquisitionCost && (
                <div className="flex justify-between items-start">
                  <div className="text-sm font-medium text-gray-500 flex items-center">
                    <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                    Cost
                  </div>
                  <div className="text-sm text-gray-900 text-right">
                    {formatCurrency(item.acquisitionCost)}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between items-start">
                <div className="text-sm font-medium text-gray-500">Quantity</div>
                <div className="text-sm text-gray-900 text-right">
                  {item.quantity || 1}
                </div>
              </div>
              
              {item.tags && item.tags.length > 0 && (
                <div className="flex justify-between items-start">
                  <div className="text-sm font-medium text-gray-500 flex items-center">
                    <TagIcon className="h-4 w-4 mr-1" />
                    Tags
                  </div>
                  <div className="text-sm text-gray-900 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      {item.tags.map((tag, index) => (
                        <span key={index} className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-between items-start">
                <div className="text-sm font-medium text-gray-500">Created</div>
                <div className="text-sm text-gray-900 text-right">
                  {new Date(item.createdAt).toLocaleDateString()}
                </div>
              </div>
              
              <div className="flex justify-between items-start">
                <div className="text-sm font-medium text-gray-500">Last Updated</div>
                <div className="text-sm text-gray-900 text-right">
                  {new Date(item.updatedAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main content area */}
        <div className="lg:col-span-2">
          {isEditing ? (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Edit Item</h3>
                <form onSubmit={handleUpdateItem} className="mt-5 space-y-6">
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    {/* Title */}
                    <div className="sm:col-span-6">
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                        Title *
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="title"
                          id="title"
                          required
                          value={editData.title}
                          onChange={handleInputChange}
                          className="form-input"
                        />
                      </div>
                    </div>
                    
                    {/* Description */}
                    <div className="sm:col-span-6">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <div className="mt-1">
                        <textarea
                          id="description"
                          name="description"
                          rows={4}
                          value={editData.description}
                          onChange={handleInputChange}
                          className="form-input"
                        />
                      </div>
                    </div>
                    
                    {/* UPC, Category */}
                    <div className="sm:col-span-3">
                      <label htmlFor="upc" className="block text-sm font-medium text-gray-700">
                        UPC/Barcode
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="upc"
                          id="upc"
                          value={editData.upc}
                          onChange={handleInputChange}
                          className="form-input"
                        />
                      </div>
                    </div>
                    
                    <div className="sm:col-span-3">
                      <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                        Category
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="category"
                          id="category"
                          value={editData.category}
                          onChange={handleInputChange}
                          className="form-input"
                        />
                      </div>
                    </div>
                    
                    {/* Bin Code, Location Code */}
                    <div className="sm:col-span-3">
                      <label htmlFor="binCode" className="block text-sm font-medium text-gray-700">
                        Bin Code
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="binCode"
                          id="binCode"
                          value={editData.binCode}
                          onChange={handleInputChange}
                          className="form-input"
                        />
                      </div>
                    </div>
                    
                    <div className="sm:col-span-3">
                      <label htmlFor="locationCode" className="block text-sm font-medium text-gray-700">
                        Location Code
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="locationCode"
                          id="locationCode"
                          value={editData.locationCode}
                          onChange={handleInputChange}
                          className="form-input"
                        />
                      </div>
                    </div>
                    
                    {/* Status, List */}
                    <div className="sm:col-span-3">
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                        Status
                      </label>
                      <div className="mt-1">
                        <select
                          id="status"
                          name="status"
                          value={editData.status}
                          onChange={handleInputChange}
                          className="form-input"
                        >
                          <option value="AVAILABLE">Available</option>
                          <option value="RESERVED">Reserved</option>
                          <option value="SOLD">Sold</option>
                          <option value="ARCHIVED">Archived</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="sm:col-span-3">
                      <label htmlFor="listId" className="block text-sm font-medium text-gray-700">
                        List
                      </label>
                      <div className="mt-1">
                        <select
                          id="listId"
                          name="listId"
                          value={editData.listId}
                          onChange={handleInputChange}
                          className="form-input"
                        >
                          <option value="">None</option>
                          {lists?.map(list => (
                            <option key={list.id} value={list.id}>
                              {list.name} {list.defaultList && '(Default)'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Consignor, Tags */}
                    <div className="sm:col-span-3">
                      <label htmlFor="consignorId" className="block text-sm font-medium text-gray-700">
                        Consignor
                      </label>
                      <div className="mt-1">
                        <select
                          id="consignorId"
                          name="consignorId"
                          value={editData.consignorId}
                          onChange={handleInputChange}
                          className="form-input"
                        >
                          <option value="">None</option>
                          {consignors?.map(consignor => (
                            <option key={consignor.id} value={consignor.id}>
                              {consignor.firstName} {consignor.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="sm:col-span-3">
                      <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                        Tags (comma separated)
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="tags"
                          id="tags"
                          value={editData.tags}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="tag1, tag2, tag3"
                        />
                      </div>
                    </div>
                    
                    {/* Quantity */}
                    <div className="sm:col-span-3">
                      <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                        Quantity
                      </label>
                      <div className="mt-1">
                        <input
                          type="number"
                          name="quantity"
                          id="quantity"
                          min="1"
                          step="1"
                          value={editData.quantity}
                          onChange={handleInputChange}
                          className="form-input"
                        />
                      </div>
                    </div>
                    
                    <div className="sm:col-span-3">
                      <label htmlFor="estimatedValue" className="block text-sm font-medium text-gray-700">
                        Estimated Value
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          name="estimatedValue"
                          id="estimatedValue"
                          min="0"
                          step="0.01"
                          value={editData.estimatedValue}
                          onChange={handleInputChange}
                          className="form-input pl-7"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    
                    <div className="sm:col-span-3">
                      <label htmlFor="acquisitionCost" className="block text-sm font-medium text-gray-700">
                        Acquisition Cost
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          name="acquisitionCost"
                          id="acquisitionCost"
                          min="0"
                          step="0.01"
                          value={editData.acquisitionCost}
                          onChange={handleInputChange}
                          className="form-input pl-7"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="btn-secondary mr-3"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={updateItemMutation.isLoading}
                    >
                      {updateItemMutation.isLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">{item.title}</h3>
                {item.description ? (
                  <div className="mt-4 prose prose-sm max-w-none text-gray-700">
                    <p>{item.description}</p>
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-gray-500 italic">No description provided</p>
                )}
              </div>
            </div>
          )}
          
          {/* Related items section could go here */}
        </div>
      </div>
    </div>
  );
};

export default InventoryDetailPage;
